import * as z from 'zod';

export const BudgetRepeatEnum = z.enum(['monthly', 'once']);
export type BudgetRepeatType = z.infer<typeof BudgetRepeatEnum>;

export const selectBudgetSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  category: z.string(),
  repeat: z.enum(['monthly', 'once']),
  value: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Budget = z.infer<typeof selectBudgetSchema>;

export const selectBudgetDetailSchema = selectBudgetSchema.extend({
  transactionsSum: z.number(),
});
export type BudgetDetail = z.infer<typeof selectBudgetDetailSchema>;

export type Category = {
  id: number;
  uuid: string;
  name: string;
};

export const transactionSelectSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  user_id: z.number(),
  family_id: z.number().nullable(),
  account_id: z.union([z.number(), z.string()]),
  status: z.enum(['posted', 'pending']),
  made_on: z.string(), // "2024-06-25 00:00:00"
  amount: z.coerce.number(),
  amount_default_currency: z.coerce.number(),
  currency: z.string(),
  default_currency: z.string(),
  category: z.string(),
  merchant: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  closing_balance: z.coerce.number().nullable().optional(),
  connection_id: z.number().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  has_receipt: z.boolean().optional(),
  is_deleted: z.boolean().optional(),
  is_duplicate: z.boolean().optional(),
  original_transaction_uuid: z.string().uuid().nullable().optional(),
  recurring_transaction_id: z.number().nullable().optional(),
  transaction_id: z.string().nullable().optional(),
  // Legacy optional fields still used in some UI flows.
  userId: z.number().optional(),
  account: z.string().optional(),
  account_type: z.custom<AccountType>().optional(),
  nature: z.custom<Nature>().optional(),
});

export type TransactionSelect = z.infer<typeof transactionSelectSchema>;

export const futureTransactionSchema = z.object({
  uuid: z.string().uuid(),
  user_id: z.number(),
  family_id: z.number().nullable(),
  account_id: z.string().nullable(),
  description: z.string().nullable().optional(),
  category: z.string(),
  merchant: z.string(),
  amount: z.coerce.number(),
  amount_default_currency: z.coerce.number(),
  default_currency: z.string(),
  currency: z.string(),
  status: z.enum(['posted', 'pending']),
  made_on: z.string(),
  recurring_transaction_id: z.number(),
  is_main_income: z.boolean().nullable(),
  nature: z.custom<Nature>().optional(),
});

export type FutureTransaction = z.infer<typeof futureTransactionSchema>;

export const transactionSchema: z.ZodType<Transaction> = z.lazy(() =>
  transactionSelectSchema.extend({
    // Override fields that differ from transactionSelectSchema
    family_id: z.number().nullable().optional(),
    account_id: z.coerce.number(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    // Splits-specific fields not present in transactionSelectSchema
    has_splits: z.boolean().optional(),
    parent_transaction_uuid: z.string().uuid().nullable().optional(),
    splits: z.array(transactionSchema).optional(),
  }),
);

export type Transaction = {
  // Core identity
  id: number;
  uuid: string;
  user_id: number;
  family_id?: number | null;
  account_id: number;
  // Status & timing
  status: 'posted' | 'pending';
  made_on: string;
  created_at?: string;
  updated_at?: string;
  // Amounts
  amount: number;
  amount_default_currency: number;
  currency: string;
  default_currency: string;
  closing_balance?: number | null;
  // Categorisation
  category: string;
  merchant?: string | null;
  description?: string | null;
  // Relationships & metadata
  transaction_id?: string | null;
  parent_transaction_uuid?: string | null;
  original_transaction_uuid?: string | null;
  connection_id?: number | null;
  recurring_transaction_id?: number | null;
  account?: string;
  account_type?: AccountType;
  nature?: Nature;
  userId?: number;
  // Flags
  has_receipt?: boolean;
  has_splits?: boolean;
  is_deleted?: boolean;
  is_duplicate?: boolean;
  // Recursive splits
  splits?: Transaction[];
};

export type RecurringTransaction = {
  id: number;
  uuid: string;
  user_id: number;
  family_id: number;
  account_id: string; // converted from bigint
  description: string;
  category: string;
  merchant: string;
  amount: number;
  currency: string;
  is_main_income?: boolean;

  // Recurrence specific fields
  frequency: 'monthly' | 'yearly';
  day_of_month?: number | null; // 1-31, null means end of month
  start_date: string;
  end_date: string | null; // null means indefinite
  last_generated_date: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type TransactionSaltedge = {
  id: bigint;
  account_id: bigint;
  duplicated?: boolean;
  description?: string;
  mode: string;
  status: 'posted' | 'pending';
  made_on: string; // "2024-06-25 00:00:00";
  amount: number;
  currency: string; //'USD';
  name: string;
  nature?: Nature;
  // todo categorytype/enum
  category: string;
  merchant?: string;
  extra: {
    merchant_id: string;
    account_balance_snapshot: number;
    categorization_confidence: number;
    customer_category_code: string;
    customer_category_name: string;
    original_category: string;
  };
  created_at: Date; //'2024-05-16T09:23:51Z';
  updated_at: Date; //'2024-05-16T09:23:51Z';
};

export type BankAccount = {
  id: number;
  uuid: string;
  name: string;
  label?: string;
  balance: number;
};

export type Creditcard = {
  id: number;
  uuid: string;
  issuer: string;
  label?: string;
  balance: number;
};

export type DefaultAccount = {
  id: number;
  name: string;
  img: string;
  type: AccountType;
};

export type Connection = {
  id: number;
  provider_name: string;
  provider_id: number;
  user_id: number;
  connection_id: bigint;
  last_update: null | string;
  status: 'active' | 'inactive' | 'disabled';
};

export type SaltEdgeConnection = {
  id: bigint;
  provider_name: string;
  provider_id: number;
  status: 'active' | 'inactive' | 'disabled';
};

export type Provider = {
  id: number;
  name: string;
  logo_url: string;
  status: 'active' | 'inactive' | 'disabled';
  home_url: string;
  login_url: string;
  updated_at: string;
};

export type ProviderAccounts = {
  id: number;
  account_id: string;
  name: string;
  connection_id: string;
  logo_url: string;
  status: 'active' | 'inactive' | 'disabled';
  last_update: string | null;
  accounts: ProviderAccount[];
};

export type Nature =
  | 'account'
  | 'bonus'
  | 'card'
  | 'checking'
  | 'credit'
  | 'credit_card'
  | 'debit_card'
  | 'ewallet'
  | 'insurance'
  | 'investment'
  | 'loan'
  | 'mortgage'
  | 'cash_docomondo'
  | 'savings';

export type AccountType = 'saltedge' | 'manual' | 'cash';

export type Account = {
  id: number;
  account_id: string | null;
  balance: string;
  name: string;
  displayName: string | null;
  nature: Nature;
  currency: string;
  account_type: AccountType;
};
export type ProviderAccount = Account & {
  account_id: string;
};

export type Period = '1_week' | '1_month' | '3_months' | '1_year' | 'custom';

export type AMWUser = {
  id: number;
  email: string;
  name: string;
  customerId: bigint | null;
  defaultCurrency: string | null;
};

export type SaltedgeCategories = {
  personal: {
    [key: string]: string[];
  };
  business: {
    [key: string]: string[];
  };
};

export type Color =
  | 'slate'
  | 'gray'
  | 'zinc'
  | 'neutral'
  | 'stone'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | undefined;

export const userSchema = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string(),
  customerId: z.string().nullable(),
  defaultCurrency: z.string().nullable(),
});

export const familySchema = z
  .object({
    id: z.number(),
    name: z.string(),
    created_by: z.number(),
    members: z.array(
      z.object({
        id: z.number(),
        role: z.string(),
        status: z.enum(['active', 'inactive', 'pending', 'rejected']),
        created_at: z.string(),
        updated_at: z.string(),
        user: z.object({
          id: z.number(),
          email: z.string(),
          name: z.string(),
        }),
      }),
    ),
  })
  .nullable();

export const familyInviteSchema = z.object({
  id: z.number(),
  updated_at: z.string(),
  memberId: z.number(),
  creator: z.object({
    email: z.string(),
    id: z.number(),
    name: z.string(),
  }),
});

export const connectionSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  provider_id: z.number(),
  provider_name: z.string(),
  connection_id: z.string(),
  status: z.string(),
  last_update: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export interface ExtractedReceiptData {
  merchant_name: string;
  date: string; // ISO 8601
  total_amount: number;
  currency: string;
  tax_amount: number;
  category: 'Food' | 'Transport' | 'Hotel' | 'Other';
}

export interface ReceiptAnalyzeResponse {
  s3Key: string; // The path where the file was saved
  extractedData: ExtractedReceiptData | { error: string };
}

export type CsvJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface CsvJobSummary {
  jobId: string;
  filename: string;
  status: CsvJobStatus;
  error?: string;
  accountId?: number;
}

export interface CsvJobResponse {
  jobId: string;
  status: CsvJobStatus;
  downloadUrl?: string;
  error?: string;
}

export interface CsvTransaction {
  merchant_name: string;
  date: string;
  total_amount: number;
  currency: string;
  tax_rate_percent: number | null;
  tax_amount: number;
  category: string | null;
}

export interface CsvImportResult {
  total: number;
  transactions: CsvTransaction[];
}

export interface CsvTransactionCreate {
  merchant: string;
  made_on: string;
  amount: number;
  currency: string;
  category: string | null;
  description?: string;
  accountId?: number;
}

export const calendarDaySchema = z.object({
  date: z.string(),
  has_income: z.boolean(),
  has_expense: z.boolean(),
});
export type CalendarDay = z.infer<typeof calendarDaySchema>;

export const calendarSummarySchema = z.object({
  upcoming_bank_expenses: z.number(),
  credit_card_balance: z.number(),
  currency: z.string(),
});
export type CalendarSummary = z.infer<typeof calendarSummarySchema>;

export type ApiErrorCode =
  | 'generic'
  | 'TRANSACTION_VALIDATION_FAILED'
  | 'BUDGET_TRANSACTION_NOT_FOUND'
  | 'BUDGET_USER_NOT_FOUND'
  | 'REQUEST_BAD'
  | 'SYSTEM_INTERNAL_ERROR'
  | 'FAMILY_INVITE_CONFLICT'
  | 'ACCOUNT_NOT_FOUND'
  | 'EXPENSES_LIST_MISSING_PARAMETERS'
  | 'EXPENSES_USER_NOT_FOUND'
  | 'EXPENSES_ORGANIZATION_NOT_FOUND'
  | 'AUTH_UNAUTHORIZED'
  | 'AUTH_FORBIDDEN'
  | 'ORGANIZATION_NOT_FOUND'
  | 'FAMILY_MEMBER_NOT_FOUND'
  | 'FAMILY_REMOVE_SELF_FORBIDDEN'
  | 'FAMILY_INVITE_NOT_FOUND'
  | 'BUDGET_NOT_FOUND'
  | 'CONNECTION_NOT_FOUND'
  | 'RECURRING_TRANSACTION_NOT_FOUND'
  | 'RECEIPT_ANALYSIS_FAILED'
  | 'CSV_PARSE_FAILED'
  | 'FILE_TOO_LARGE'
  | 'REQUEST_VALIDATION_FAILED'
  | 'TRANSACTION_CREATION_FAILED'
  | 'ACCOUNTS_NOT_FOUND'
  | 'ANALYTICS_INVALID_TYPE'
  | 'ANALYTICS_INVALID_PERIOD'
  | 'REQUEST_MISSING_PARAMETERS'
  | 'STATIC_VALIDATION_NUMBER'
  | 'STATIC_VALIDATION_REQUIRED'
  | 'STATIC_VALIDATION_REQUIRED_NAME'
  | 'STATIC_VALIDATION_REQUIRED_CURRENCY'
  | 'STATIC_ERROR_MAIN_INCOME'
  | 'STATIC_CSV_IMPORT_FILE_TOO_LARGE'
  | 'STATIC_CSV_IMPORT_INVALID_TYPE'
  | 'STATIC_CSV_IMPORT_UPLOAD_ERROR'
  | 'STATIC_CSV_IMPORT_IMPORT_ERROR'
  | 'STATIC_CSV_IMPORT_INVALID_DATE_ERROR'
  | 'STATIC_CSV_IMPORT_ACCOUNT_REQUIRED'
  | 'STATIC_CSV_IMPORT_PARTIAL_ERROR'
  | 'STATIC_CREDIT_CARD_SOME_FAILED'
  | 'STATIC_CREDIT_CARD_FAILED_EXPENSES'
  | 'STATIC_CREDIT_CARD_FAILED_BATCH'
  | 'STATIC_CREDIT_CARD_VALID_AMOUNT'
  | 'STATIC_SPLIT_MAX_SPLITS_REACHED'
  | 'STATIC_SPLIT_SUM_ERROR'
  | 'STATIC_RECEIPT_LOAD_PREVIEW_ERROR'
  | 'STATIC_RECEIPT_DELETE_ERROR'
  | 'STATIC_RECEIPT_FILE_TOO_LARGE'
  | 'STATIC_RECEIPT_INVALID_FILE_TYPE'
  | 'STATIC_RECEIPT_UPLOAD_ERROR'
  | 'STATIC_RECEIPT_ANALYSIS_FAILED_PREFIX'
  | 'STATIC_RECEIPT_ANALYSIS_ERROR'
  | 'STATIC_BUDGET_DELETE_ERROR'
  | 'STATIC_SNAPSHOTS_INIT_ERROR';
